import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Checkbox,
  CheckboxGroup,
  Stack,
  Divider,
  IconButton,
  useBreakpointValue,
  OrderedList,
  ListItem,
  UnorderedList,
  RadioGroup,
  Radio,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon, StarIcon, TimeIcon } from '@chakra-ui/icons';
import { FiFilter } from 'react-icons/fi';
import { useFindMany } from '@gadgetinc/react';
import { api } from '../api';

// Mock data structure for demonstration - this would come from the actual model when properly implemented
const mockContentStructure = {
  question: {
    type: 'question',
    title: 'Sample Medical Question',
    subject: 'Cardiology',
    difficulty: 'intermediate',
    question: 'What is the most common cause of chest pain in emergency departments?',
    options: [
      'Myocardial infarction',
      'Gastroesophageal reflux',
      'Musculoskeletal pain',
      'Pulmonary embolism'
    ],
    correctAnswer: 2,
    explanation: 'Musculoskeletal pain is actually the most common cause of chest pain presentations in emergency departments, though cardiac causes must always be ruled out first.',
    tags: ['chest pain', 'emergency medicine', 'differential diagnosis'],
    usageCount: 45,
    difficulty_level: 'intermediate'
  },
  scenario: {
    type: 'scenario',
    title: 'Cardiac Arrest Scenario',
    subject: 'Emergency Medicine',
    difficulty: 'advanced',
    description: 'A 65-year-old male collapses in the waiting room. Staff witnesses the collapse and notes he is unresponsive.',
    learningObjectives: [
      'Recognize cardiac arrest',
      'Initiate CPR immediately',
      'Apply AED when available',
      'Follow ACLS protocols'
    ],
    keyPoints: [
      'Time is critical in cardiac arrest',
      'High-quality CPR is essential',
      'Early defibrillation improves outcomes'
    ],
    tags: ['cardiac arrest', 'CPR', 'ACLS', 'emergency response'],
    usageCount: 23,
    difficulty_level: 'advanced'
  }
};

const EducationalContent = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContentType, setSelectedContentType] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedContent, setSelectedContent] = useState(null);
  const [bookmarkedItems, setBookmarkedItems] = useState(new Set());

  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  // Fetch educational content
  const [{ data: educationalContent, fetching, error }] = useFindMany(api.educationalContent, {
    first: 50,
    sort: { createdAt: sortBy === 'newest' ? 'Descending' : 'Ascending' },
    ...(searchTerm && { search: searchTerm })
  });

  const isMobile = useBreakpointValue({ base: true, md: false });

  // Mock content types and subjects - these would come from the database in a real implementation
  const contentTypes = [
    { value: 'all', label: 'All Content' },
    { value: 'question', label: 'Practice Questions' },
    { value: 'scenario', label: 'Clinical Scenarios' },
    { value: 'explanation', label: 'Concept Explanations' },
    { value: 'procedure', label: 'Procedures' }
  ];

  const subjects = [
    { value: 'all', label: 'All Subjects' },
    { value: 'cardiology', label: 'Cardiology' },
    { value: 'emergency', label: 'Emergency Medicine' },
    { value: 'respiratory', label: 'Respiratory' },
    { value: 'neurology', label: 'Neurology' },
    { value: 'pharmacology', label: 'Pharmacology' }
  ];

  const difficultyLevels = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  // Mock processed content - in reality this would be processed from the actual model data
  const processedContent = useMemo(() => {
    if (!educationalContent) return [];
    
    // Since the actual model doesn't have content fields yet, we'll create mock data
    // This would be replaced with actual data processing when the model is properly implemented
    return educationalContent.map((item, index) => ({
      ...item,
      // Mock content structure based on index to show variety
      ...(index % 2 === 0 ? {
        ...mockContentStructure.question,
        id: item.id,
        title: `Practice Question ${index + 1}`,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      } : {
        ...mockContentStructure.scenario,
        id: item.id,
        title: `Clinical Scenario ${index + 1}`,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt
      })
    }));
  }, [educationalContent]);

  // Filter and sort content
  const filteredContent = useMemo(() => {
    let filtered = processedContent || [];

    if (selectedContentType !== 'all') {
      filtered = filtered.filter(item => item.type === selectedContentType);
    }

    if (selectedSubject !== 'all') {
      filtered = filtered.filter(item => 
        item.subject?.toLowerCase().includes(selectedSubject.toLowerCase())
      );
    }

    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(item => item.difficulty_level === selectedDifficulty);
    }

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort content
    switch (sortBy) {
      case 'mostUsed':
        filtered.sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0));
        break;
      case 'alphabetical':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      default: // newest
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return filtered;
  }, [processedContent, selectedContentType, selectedSubject, selectedDifficulty, searchTerm, sortBy]);

  const handleViewContent = (content) => {
    setSelectedContent(content);
    onModalOpen();
    // In a real implementation, you would track usage here
    // await api.educationalContent.update({ id: content.id, usageCount: (content.usageCount || 0) + 1 });
  };

  const toggleBookmark = (contentId) => {
    setBookmarkedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contentId)) {
        newSet.delete(contentId);
      } else {
        newSet.add(contentId);
      }
      return newSet;
    });
  };

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'question': return 'blue';
      case 'scenario': return 'green';
      case 'explanation': return 'purple';
      case 'procedure': return 'orange';
      default: return 'gray';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'green';
      case 'intermediate': return 'yellow';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  const renderContentPreview = (content) => {
    switch (content.type) {
      case 'question':
        return (
          <Text fontSize="sm" noOfLines={2}>
            {content.question}
          </Text>
        );
      case 'scenario':
        return (
          <Text fontSize="sm" noOfLines={2}>
            {content.description}
          </Text>
        );
      default:
        return (
          <Text fontSize="sm" color="gray.500">
            {content.type} content
          </Text>
        );
    }
  };

  const renderDetailedContent = (content) => {
    if (!content) return null;

    switch (content.type) {
      case 'question':
        return (
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="bold" mb={2}>Question:</Text>
              <Text>{content.question}</Text>
            </Box>
            
            <Box>
              <Text fontWeight="bold" mb={2}>Options:</Text>
              <RadioGroup>
                <Stack spacing={2}>
                  {content.options?.map((option, index) => (
                    <Radio 
                      key={index} 
                      value={index.toString()}
                      isChecked={index === content.correctAnswer}
                      colorScheme={index === content.correctAnswer ? 'green' : 'gray'}
                    >
                      {option}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>Explanation:</Text>
              <Text>{content.explanation}</Text>
            </Box>
          </VStack>
        );

      case 'scenario':
        return (
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontWeight="bold" mb={2}>Scenario:</Text>
              <Text>{content.description}</Text>
            </Box>
            
            <Box>
              <Text fontWeight="bold" mb={2}>Learning Objectives:</Text>
              <UnorderedList>
                {content.learningObjectives?.map((objective, index) => (
                  <ListItem key={index}>{objective}</ListItem>
                ))}
              </UnorderedList>
            </Box>

            <Box>
              <Text fontWeight="bold" mb={2}>Key Points:</Text>
              <UnorderedList>
                {content.keyPoints?.map((point, index) => (
                  <ListItem key={index}>{point}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          </VStack>
        );

      default:
        return <Text>Content details not available</Text>;
    }
  };

  const FiltersPanel = () => (
    <VStack align="stretch" spacing={4} p={4}>
      <Heading size="md">Filters</Heading>
      
      <Box>
        <Text fontWeight="semibold" mb={2}>Content Type</Text>
        <Select value={selectedContentType} onChange={(e) => setSelectedContentType(e.target.value)}>
          {contentTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </Select>
      </Box>

      <Box>
        <Text fontWeight="semibold" mb={2}>Subject</Text>
        <Select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}>
          {subjects.map(subject => (
            <option key={subject.value} value={subject.value}>{subject.label}</option>
          ))}
        </Select>
      </Box>

      <Box>
        <Text fontWeight="semibold" mb={2}>Difficulty</Text>
        <Select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
          {difficultyLevels.map(level => (
            <option key={level.value} value={level.value}>{level.label}</option>
          ))}
        </Select>
      </Box>

      <Box>
        <Text fontWeight="semibold" mb={2}>Sort By</Text>
        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="mostUsed">Most Used</option>
          <option value="alphabetical">Alphabetical</option>
        </Select>
      </Box>
    </VStack>
  );

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading educational content: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>Educational Content Library</Heading>
          <Text color="gray.600">
            Browse and explore educational materials, practice questions, and clinical scenarios
          </Text>
        </Box>

        {/* Search and Controls */}
        <Flex direction={{ base: 'column', md: 'row' }} gap={4} align={{ md: 'center' }}>
          <InputGroup flex={1} maxW={{ md: '400px' }}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search content, tags, or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {isMobile && (
            <Button leftIcon={<FiFilter />} onClick={onFilterOpen}>
              Filters
            </Button>
          )}

          <HStack spacing={4}>
            <Stat size="sm">
              <StatLabel>Content Found</StatLabel>
              <StatNumber>{filteredContent.length}</StatNumber>
            </Stat>
          </HStack>
        </Flex>

        {/* Main Content */}
        <Grid templateColumns={{ base: '1fr', md: '250px 1fr' }} gap={6}>
          {/* Desktop Filters Sidebar */}
          {!isMobile && (
            <GridItem>
              <Box position="sticky" top={4} bg="gray.50" borderRadius="md">
                <FiltersPanel />
              </Box>
            </GridItem>
          )}

          {/* Content Grid */}
          <GridItem>
            {fetching ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : filteredContent.length === 0 ? (
              <Alert status="info">
                <AlertIcon />
                No educational content found. Try adjusting your filters or search terms.
              </Alert>
            ) : (
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                {filteredContent.map((content) => (
                  <Card key={content.id} cursor="pointer" _hover={{ shadow: 'md' }}>
                    <CardHeader pb={2}>
                      <Flex justify="space-between" align="start">
                        <VStack align="start" flex={1} spacing={1}>
                          <Heading size="sm" noOfLines={2}>
                            {content.title}
                          </Heading>
                          <HStack wrap="wrap" spacing={2}>
                            <Badge colorScheme={getContentTypeColor(content.type)}>
                              {content.type}
                            </Badge>
                            <Badge colorScheme={getDifficultyColor(content.difficulty_level)}>
                              {content.difficulty_level}
                            </Badge>
                          </HStack>
                        </VStack>
                        <IconButton
                          icon={<StarIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme={bookmarkedItems.has(content.id) ? 'yellow' : 'gray'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBookmark(content.id);
                          }}
                        />
                      </Flex>
                    </CardHeader>

                    <CardBody pt={0}>
                      <VStack align="stretch" spacing={3}>
                        {renderContentPreview(content)}
                        
                        <HStack justify="space-between" fontSize="sm" color="gray.500">
                          <HStack spacing={4}>
                            <HStack>
                              <ViewIcon />
                              <Text>{content.usageCount || 0} views</Text>
                            </HStack>
                            <HStack>
                              <TimeIcon />
                              <Text>{new Date(content.createdAt).toLocaleDateString()}</Text>
                            </HStack>
                          </HStack>
                        </HStack>

                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleViewContent(content)}
                        >
                          View Details
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </Grid>
            )}
          </GridItem>
        </Grid>
      </VStack>

      {/* Mobile Filters Drawer */}
      <Drawer isOpen={isFilterOpen} placement="left" onClose={onFilterClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Content Filters</DrawerHeader>
          <DrawerBody>
            <FiltersPanel />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Content Detail Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={2}>
              <Text>{selectedContent?.title}</Text>
              <HStack>
                <Badge colorScheme={getContentTypeColor(selectedContent?.type)}>
                  {selectedContent?.type}
                </Badge>
                <Badge>{selectedContent?.subject}</Badge>
                <Badge colorScheme={getDifficultyColor(selectedContent?.difficulty_level)}>
                  {selectedContent?.difficulty_level}
                </Badge>
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {renderDetailedContent(selectedContent)}
            
            {selectedContent?.tags && (
              <Box mt={6}>
                <Text fontWeight="bold" mb={2}>Tags:</Text>
                <HStack wrap="wrap">
                  {selectedContent.tags.map((tag, index) => (
                    <Badge key={index} variant="outline">
                      {tag}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                leftIcon={<StarIcon />}
                variant="outline"
                onClick={() => toggleBookmark(selectedContent?.id)}
                colorScheme={bookmarkedItems.has(selectedContent?.id) ? 'yellow' : 'gray'}
              >
                {bookmarkedItems.has(selectedContent?.id) ? 'Bookmarked' : 'Bookmark'}
              </Button>
              <Button colorScheme="blue" onClick={onModalClose}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default EducationalContent;